/**
 * The tenant's display name for the coin wallet.
 *
 * BF4 lets a tenant rename "Coins" → "UPCoin" / "Gems" / "Reward Points" via
 * `tenant_config.features.coin_label`. The admin and member SPAs already honour
 * it; the till used to hardcode the word "coin"/"coins", so a cashier on a
 * renamed tenant saw a currency name that appears nowhere else in the product.
 *
 * Kept free of react-query and of `./api` so the pure helpers stay importable
 * from a plain (non-DOM) unit test and so `./api` can reuse the default without
 * an import cycle.
 */
import { currencyWord } from '@mlm/shared';

/** Mirrors the server default (`tenant-features.ts` → `coin_label: 'Coins'`). */
export const DEFAULT_COIN_LABEL = 'Coins';

const LEDGER_KINDS = ['cash', 'gc', 'token', 'coin', 'ai_credit'] as const;

/**
 * Ledger currency kind → the word the cashier reads. Delegates to the shared
 * `currencyWord` so a future `gc_label` is a one-file change; an unrecognised
 * kind falls back to the bare code rather than throwing at the till.
 *
 * Note `cash` maps to "Cash", NOT "₱" — call sites that want the peso symbol
 * branch on cash before calling this.
 */
export function walletWord(currency: string, coinLabel: string): string {
  return (LEDGER_KINDS as readonly string[]).includes(currency)
    ? currencyWord(currency as (typeof LEDGER_KINDS)[number], coinLabel)
    : currency.toUpperCase();
}
